import {makeAutoObservable, reaction, runInAction} from 'mobx';
import {
    AdminOrganizationDTO,
    ContactDTO,
    DeviceResponseDTO,
    FilterDTO,
    FilterGroupDTO,
    SiteEfficiencyDTO,
    SiteInfoDTO
} from '../Models/ApiModels';
import { LocalStorageGet, LocalStorageSet } from '../Services/Helpers';
import { PromiseCompletion } from '../Services/PromiseCompletion';
import { getSelectOptions, Option } from '../Components';
import { ApiService } from '../Services';
import {ApiURL} from '../AppConstants';
import DateTimeService from "../Services/DateTimeService";
import {authService} from "../index";

export type SelectedFilter = {
    customer: string
    site: string
    plant: string
}

const FILTER_STORE_KEY = 'filterState';

export class FiltersStore {
    public filters: FilterDTO = new FilterDTO();
    public sites: SiteInfoDTO[] = [];
    public siteStatuses: SiteEfficiencyDTO[] = [];
    public publicSites: ContactDTO[] = [];
    public adminOrganizationSettings: AdminOrganizationDTO[] = [];
    public isIoTSubscription: boolean = false;
    public selected: SelectedFilter = this.defaultFilter;
    private _filterChangeHandler: Function | null = null;
    isLoading: PromiseCompletion = new PromiseCompletion();
    public isAdmin = authService.isAdmin;
    public isGlobalSupport = authService.isGlobalSupport;

    constructor () {
        makeAutoObservable(this);
        reaction(
            () => this.selected.customer,
            (customer: string) => {
                if (customer) {
                    if(this.isAdmin || this.isGlobalSupport){
                        void this._getFilterData(customer);
                    }
                    void this._getSiteStatus();
                    this.isIoTSubscription = this.adminOrganizationSettings?.find?.(org => org.id === customer)?.settings?.['subscriptions']?.includes('IoT')??false;
                }
            }
        );
    }

    public async getData () {
        await this._getCustomers();
        if(!this.isAdmin && !this.isGlobalSupport){
            await this._getFilterData();
        }
        this.restoreState();
        await this._getSiteStatus();

    }

    private async _getCustomers () {
        const [{data: customers}, {data: adminOrganizationSettings}] = await Promise.all([ApiService.getData<FilterGroupDTO[]>(ApiURL.Organizations, null, { completion: this.isLoading }), ApiService.getData<AdminOrganizationDTO[]>(ApiURL.AdminOrganizationSettings, null, { completion: this.isLoading }, true)]);

        runInAction(() => {
            this.filters.customers = customers;
            this.adminOrganizationSettings = adminOrganizationSettings;

        });
    }

    private async _getFilterData (organizationId?: string) {
        const params = organizationId ? {organizationId} : undefined;
        const siteReq = ApiService.getData<SiteInfoDTO[]>(ApiURL.Sites, params);
        const deviceReq = ApiService.getData<DeviceResponseDTO[]>(ApiURL.Devices, params);
        const contactsReq = ApiService.getData<ContactDTO[]>(ApiURL.Contacts, null);
        const getData = Promise.all([siteReq, deviceReq, contactsReq]);
        this.isLoading.subscribe(getData);
        const [
            {data:site},
            {data:device}, {data: contacts}] = await getData;
        runInAction(() => {
            this.sites = site;
            this.publicSites = contacts;
            this.filters.sites = site.map(s => {
                return {
                    name: s.name,
                    id: s.id,
                };
            });
            this.filters.plants = device.map(d => {
                const selected = site.find(s => s.id === d.siteId);
                return {
                    name: d.name,
                    id: d.id,
                    siteId: selected!.id,
                };
            });
        });
    }

    public async _getSiteStatus () {
        if (!this.selected.customer) {
            return;
        }
        try {
            const siteStatusRequest = ApiService.getData<SiteEfficiencyDTO[]>(ApiURL.SiteEfficiency, {
                organizationId: this.selected.customer,
                timeTo:DateTimeService.now(),
                timeFrom:DateTimeService.startOfDay(DateTimeService.addDays(DateTimeService.now(), -28)),
            });
            const requestData = Promise.all([siteStatusRequest]);
            this.isLoading.subscribe(requestData);
            const [{ data: siteStatus }] = await requestData;
            runInAction(() => {
                this.siteStatuses = siteStatus;
            });
        } catch (e) {
            console.log(e);
        }

    }

    public onResetClick = () => {
        const newFilters = this.defaultFilter;
        newFilters.customer = this.selected.customer || this.filters.customers[0]?.id || '';
        this.onFilterChange(newFilters);
        this._filterChangeHandler && this._filterChangeHandler();
    };

    public get customerOptions (): Option[] {
        return getSelectOptions<FilterGroupDTO>(this.filters?.customers || [], 'name', 'id');
    }

    public get siteOptions (): Option[] {
        const sites = this.filters.sites;
        return getSelectOptions<FilterGroupDTO>(sites, 'name', 'id');
    }

    public get plantOptions (): Option[] {
        const plants = this.filters?.plants ?? [];

        const filtered = plants.filter(e => {
            if (this.selected.site) {
                return e.siteId === this.selected.site;
            }
            return true;
        });

        const mapped = filtered.map(p => ({
            ...p,
            name: `${p.name} (${p.id})`
        }));

        return getSelectOptions<FilterGroupDTO>(mapped, 'name', 'id');
    }

    public get selectedCustomerOption () {
        return this.customerOptions.find(o => o.value === this.selected.customer);
    }

    public get selectedSiteOption () {
        return this.siteOptions.find(o => o.value === this.selected.site);
    }

    public get selectedPlantOption () {
        return this.plantOptions.find(o => o.value === this.selected.plant);
    }

    public onCustomerChange = (selected: string) => {
        const newFilters = this.defaultFilter;
        newFilters.customer = selected;
        this.onFilterChange(newFilters);
    };

    public onSiteChange = (selected: string) => {
        const newFilters = this.defaultFilter;
        const site = this.filters.sites.find(e => e.id === selected);
        newFilters.customer = this.selected.customer;
        if (selected && site) {
            newFilters.site = site.id;
        }
        this.onFilterChange(newFilters);
    };

    public onPlantChange = (selected: string) => {
        const newFilters = this.defaultFilter;
        const plant = this.filters.plants.find(e => e.id === selected);
        newFilters.customer = this.selected.customer;
        if (selected && plant) {
            newFilters.site = plant.siteId;
            newFilters.plant = plant.id;
        } else {
            newFilters.site = this.selected.site;
        }
        this.onFilterChange(newFilters);
    };

    public get defaultFilter () {
        return {
            customer: '',
            site: '',
            plant: ''
        };
    }

    public restoreState () {
        const hasValue = LocalStorageGet<SelectedFilter>(FILTER_STORE_KEY);

        if (hasValue && this.customerOptions.find(e => e.value === hasValue.customer)) {
            this.selected = hasValue;
        }
        if (!this.selected.customer && this.customerOptions.length) {
            this.onCustomerChange(this.customerOptions[0].value);
        }
    }

    public storeValue () {
        LocalStorageSet(FILTER_STORE_KEY, this.selected);
    }

    public onFilterChange (newFilters: SelectedFilter) {
        this.selected = newFilters;
        this.storeValue();
    }

    public async loadSites() {
        const {data: sites} = await ApiService.getData<SiteInfoDTO[]>(ApiURL.Sites, {organizationId: this.selected.customer}, {completion: this.isLoading});
        runInAction(() => {
            this.filters.sites = sites;
        });
    }

}